/**
 * Universal Redirect Handler
 *
 * Handles all incoming redirect URLs and routes users to appropriate flows:
 * - Authenticated users → direct redirect
 * - Unauthenticated users → auth flow with intent preservation
 * - Magic links → token validation + auth
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { redirectService } from './redirectService';

export const RedirectHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading: isAuth0Loading,
    loginWithRedirect,
  } = useAuth0();
  const [isProcessing, setIsProcessing] = useState(true);
  const [redirectIntent, setRedirectIntent] = useState<any>(null);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        // Parse the redirect intent from URL
        const intent = redirectService.parseRedirectIntent();

        if (!intent) {
          // No valid redirect intent, go to dashboard
          navigate('/', { replace: true });
          return;
        }

        setRedirectIntent(intent);

        // Store the intent for post-auth navigation
        redirectService.storeRedirectIntent(intent);

        // If user is already authenticated, execute redirect immediately
        if (isAuthenticated) {
          const success = redirectService.executeRedirect(navigate);
          if (!success) {
            navigate('/dashboard', { replace: true });
          }
          return;
        }

        // If destination requires auth and user is not authenticated
        if (redirectService.requiresAuth(intent)) {
          // Trigger auth flow - the callback will handle the redirect
          await loginWithRedirect({
            appState: {
              returnTo: location.pathname + location.search,
            },
          });
          return;
        }

        // Public destination, redirect directly
        navigate(intent.destination, { replace: true });
      } catch (error) {
        console.error('Redirect handler error:', error);
        navigate('/', { replace: true });
      } finally {
        setIsProcessing(false);
      }
    };

    // Wait for Auth0 to finish loading before processing
    if (!isAuth0Loading) {
      handleRedirect();
    }
  }, [isAuth0Loading, isAuthenticated, loginWithRedirect, navigate, location]);

  // Show loading state
  if (isAuth0Loading || isProcessing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgb(26, 26, 46)',
          color: '#F0F3FF',
          padding: '2rem',
        }}
      >
        <CircularProgress
          sx={{
            color: '#6D539F',
            marginBottom: '2rem',
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            marginBottom: '1rem',
            textAlign: 'center',
          }}
        >
          {redirectIntent?.intent === 'upgrade'
            ? 'Setting up your upgrade...'
            : redirectIntent?.intent === 'signup'
              ? 'Preparing your account...'
              : 'Redirecting you...'}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#9C9C9D',
            textAlign: 'center',
            maxWidth: '400px',
          }}
        >
          {redirectIntent?.source === 'sms' && 'Thanks for clicking the link! '}
          We're getting everything ready for you.
        </Typography>
      </Box>
    );
  }

  // This should not render as we navigate away in useEffect
  return null;
};

export default RedirectHandler;
